import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAccountingMutations, useSuppliers } from "@/hooks/useAccounting";
import type { Supplier } from "@/services/edgeFunctionWrappers/accountingManagementWrapper";

/** CRUD léger des fournisseurs — soft-delete (badge inactif) si référencé. */
export function SuppliersTab() {
  const { t } = useTranslation(["accounting", "common"]);
  const { toast } = useToast();
  const { data: suppliers = [], isLoading } = useSuppliers(true);
  const { createSupplier, updateSupplier, deleteSupplier } = useAccountingMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [deleting, setDeleting] = useState<Supplier | null>(null);
  const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", notes: "" });

  useEffect(() => {
    if (formOpen) {
      setForm(editing
        ? { name: editing.name, contact_name: editing.contact_name ?? "", phone: editing.phone ?? "", email: editing.email ?? "", notes: editing.notes ?? "" }
        : { name: "", contact_name: "", phone: "", email: "", notes: "" });
    }
  }, [formOpen, editing]);

  const onSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      if (editing) {
        await updateSupplier.mutateAsync({ id: editing.id, patch: form });
      } else {
        await createSupplier.mutateAsync(form);
      }
      toast({ title: editing ? t("suppliersTab.updated") : t("suppliersTab.created") });
      setFormOpen(false);
    } catch (e: any) {
      toast({ title: t("common:error"), description: e?.message ?? "", variant: "destructive" });
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    try {
      const res = await deleteSupplier.mutateAsync(deleting.id);
      toast({ title: (res as any)?.soft ? t("suppliersTab.deactivated") : t("suppliersTab.deleted") });
      setDeleting(null);
    } catch (e: any) {
      toast({ title: t("common:error"), description: e?.message ?? "", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" className="h-9" onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4 mr-1.5" /> {t("suppliersTab.add")}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : suppliers.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t("suppliersTab.empty")}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {suppliers.map((s) => (
            <div key={s.id} className="rounded-2xl border bg-card p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium truncate">{s.name}</div>
                {!s.is_active && <Badge variant="secondary">{t("suppliersTab.inactive")}</Badge>}
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {s.contact_name && <div>{s.contact_name}</div>}
                {s.phone && <div>{s.phone}</div>}
                {s.email && <div className="truncate">{s.email}</div>}
              </div>
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => { setEditing(s); setFormOpen(true); }} aria-label={t("common:edit", { defaultValue: "Modifier" })}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setDeleting(s)} aria-label={t("suppliersTab.delete")}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{editing ? t("suppliersTab.editTitle") : t("suppliersTab.addTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">{t("suppliersTab.name")}</label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="text-base md:text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium">{t("suppliersTab.contact")}</label>
                <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className="text-base md:text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium">{t("suppliersTab.phone")}</label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="text-base md:text-sm" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t("suppliersTab.email")}</label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="text-base md:text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("suppliersTab.notes")}</label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="min-h-[60px] text-base md:text-sm" />
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setFormOpen(false)}>{t("common:cancel")}</Button>
            <Button className="flex-1" onClick={onSubmit} disabled={!form.name.trim()}>
              {editing ? t("form.save") : t("form.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("suppliersTab.confirmDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("suppliersTab.confirmDeleteDesc", { name: deleting?.name ?? "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>{t("suppliersTab.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
